import { __decorate } from "tslib";
import { html } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { property, query, state } from 'lit/decorators.js';
import { emit } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
import styles from './dropdown.scss.js';
let counter = 0;
/**
 * @element cipublic-dropdown
 */
export class Dropdown extends CipublicElement {
    constructor() {
        super(...arguments);
        /**
         * The value of the dropdown
         */
        this.value = '';
        /**
         * Constructor name.
         */
        this._name = 'dropdown';
        /**
         * ID to associate the label with the element.
         */
        this._id = ++counter;
        /**
         * Text of the dropdown trigger button
         */
        this.buttonText = '';
    }
    connectedCallback() {
        super.connectedCallback();
        this.updateInitialChildren();
        this.addEventListener('updatechildren', this.updateInitialChildren);
        this.addEventListener('mousemove', this.hoverItem);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('updatechildren', this.updateInitialChildren);
        this.removeEventListener('mousemove', this.hoverItem);
    }
    updateInitialChildren() {
        var _a;
        this.initialChildren = Array.from(this.children).filter((element) => {
            return !(element.nodeName === 'SLOT') && !element.slot;
        });
        let selectedItem = this.initialChildren.find((item) => item.hasAttribute('selected'));
        if (!selectedItem) {
            this.initialChildren[0].setAttribute('selected', 'true');
            selectedItem = this.initialChildren[0];
        }
        const buttonText = ((_a = selectedItem.textContent) === null || _a === void 0 ? void 0 : _a.trim()) || '';
        this.updateButtonText(buttonText);
        this.value = selectedItem.getAttribute('value') || '';
        emit(this, 'dropdown-update', { detail: { element: this, value: this.value } });
    }
    updateButtonText(buttonText) {
        const words = buttonText.trim().split(/(\w+(?=\/)|\w+(?=[-])|\s|\/\/|<[>]*>|@|%)/);
        const lastWord = words[words.length - 1].length <= 1 ? words.splice(-2).join('') : words.splice(-1).join('');
        const firstPart = words.join('');
        this.buttonText = `${firstPart}<span class="dropdown-icon">${lastWord}<cipublic-icon set="system" name="caret-down"></cipublic-icon></span>`;
    }
    toggleDropdown() {
        var _a, _b, _c, _d, _e;
        (_a = this._dropdownItemsBox) === null || _a === void 0 ? void 0 : _a.classList.toggle('hidden');
        if (!((_b = this._dropdownItemsBox) === null || _b === void 0 ? void 0 : _b.classList.contains('hidden'))) {
            const minheight = parseInt(window.getComputedStyle(this._dropdownItemsBox).getPropertyValue('min-height'), 10);
            this._dropdownItemsBox.style.maxHeight = ``;
            this._dropdownItemsBox.style.minHeight = `100%`;
            const clientHeight = this._dropdownItemsBox.clientHeight;
            if (clientHeight > minheight) {
                this._dropdownItemsBox.style.minHeight = `${minheight}px`;
                const buttonTop = this._dropdownButton.getBoundingClientRect().top + this._dropdownButton.clientHeight + 24;
                this._dropdownItemsBox.style.maxHeight = `calc(100vh - ${buttonTop > 0 ? buttonTop : 0}px)`;
            }
            this._dropdownButton.setAttribute('aria-expanded', 'true');
            (_c = this._dropdownItemsBox) === null || _c === void 0 ? void 0 : _c.focus();
            const currentSelectedItem = this._dropdownItemsBox.querySelector('[aria-selected]');
            this.scrollBoxToItem(currentSelectedItem);
        }
        else {
            this._dropdownButton.removeAttribute('aria-expanded');
            (_e = (_d = this._dropdownItemsBox) === null || _d === void 0 ? void 0 : _d.querySelector('.focused')) === null || _e === void 0 ? void 0 : _e.classList.remove('focused');
        }
    }
    hideDropdown() {
        var _a, _b, _c;
        const activeElement = this.shadowRoot.querySelectorAll('button.dropdown-trigger:active');
        if (activeElement && activeElement.length === 0) {
            (_a = this._dropdownItemsBox) === null || _a === void 0 ? void 0 : _a.classList.add('hidden');
            this._dropdownButton.removeAttribute('aria-expanded');
            (_c = (_b = this._dropdownItemsBox) === null || _b === void 0 ? void 0 : _b.querySelector('.focused')) === null || _c === void 0 ? void 0 : _c.classList.remove('focused');
            const currentSelectedItem = this._dropdownItemsBox.querySelector('[aria-selected]');
            currentSelectedItem === null || currentSelectedItem === void 0 ? void 0 : currentSelectedItem.removeAttribute('aria-selected');
            const currentItem = this._dropdownItemsBox.querySelector('li.selected');
            currentItem === null || currentItem === void 0 ? void 0 : currentItem.setAttribute('aria-selected', 'true');
            this._dropdownItemsBox.setAttribute('aria-activedescendant', currentItem === null || currentItem === void 0 ? void 0 : currentItem.id);
        }
    }
    focusItem(item, keyPressed) {
        const currentFocusedItem = this._dropdownItemsBox.querySelector('li.focused');
        const currentSelectedItem = this._dropdownItemsBox.querySelector('[aria-selected]');
        if (keyPressed === 'enter') {
            const currentElement = (currentFocusedItem ? currentFocusedItem : item);
            this.selectItem(currentElement);
            this.hideDropdown();
            this._dropdownButton.focus();
            return;
        }
        if ((keyPressed === 'next' || keyPressed === 'prev') && currentFocusedItem) {
            const nextFocusElement = (keyPressed === 'next' ? currentFocusedItem === null || currentFocusedItem === void 0 ? void 0 : currentFocusedItem.nextElementSibling : currentFocusedItem === null || currentFocusedItem === void 0 ? void 0 : currentFocusedItem.previousElementSibling);
            if (nextFocusElement) {
                nextFocusElement.classList.add('focused');
                currentFocusedItem === null || currentFocusedItem === void 0 ? void 0 : currentFocusedItem.classList.remove('focused');
                currentSelectedItem === null || currentSelectedItem === void 0 ? void 0 : currentSelectedItem.removeAttribute('aria-selected');
                this.scrollBoxToItem(nextFocusElement);
                nextFocusElement.setAttribute('aria-selected', 'true');
                this._dropdownItemsBox.setAttribute('aria-activedescendant', nextFocusElement.id);
                nextFocusElement.focus();
            }
        }
        else {
            currentFocusedItem === null || currentFocusedItem === void 0 ? void 0 : currentFocusedItem.classList.remove('focused');
            currentSelectedItem === null || currentSelectedItem === void 0 ? void 0 : currentSelectedItem.removeAttribute('aria-selected');
            if (item) {
                item.classList.add('focused');
                this.scrollBoxToItem(item);
                item.setAttribute('aria-selected', 'true');
                this._dropdownItemsBox.setAttribute('aria-activedescendant', item.id);
                item.focus();
            }
        }
    }
    scrollBoxToItem(item) {
        if (this._dropdownItemsBox.scrollHeight > this._dropdownItemsBox.clientHeight) {
            const scrollTop = this._dropdownItemsBox.clientHeight + this._dropdownItemsBox.scrollTop;
            const elementTop = item.offsetTop + item.offsetHeight;
            if (elementTop > scrollTop) {
                this._dropdownItemsBox.scrollTop = elementTop - this._dropdownItemsBox.clientHeight;
            }
            else if (item.offsetTop < this._dropdownItemsBox.scrollTop) {
                this._dropdownItemsBox.scrollTop = item.offsetTop;
            }
        }
    }
    selectItem(item) {
        const { textContent, id } = item;
        const currentItem = this._dropdownItemsBox.querySelector('li.selected');
        const currentSelectedItem = this._dropdownItemsBox.querySelector('[aria-selected]');
        currentItem === null || currentItem === void 0 ? void 0 : currentItem.classList.remove('selected');
        currentSelectedItem === null || currentSelectedItem === void 0 ? void 0 : currentSelectedItem.removeAttribute('aria-selected');
        item.classList.add('selected');
        this._dropdownItemsBox.setAttribute('aria-activedescendant', id);
        item.setAttribute('aria-selected', 'true');
        const buttonText = (textContent === null || textContent === void 0 ? void 0 : textContent.trim()) || '';
        this.updateButtonText(buttonText);
        this.scrollBoxToItem(item);
        this.value = item.dataset.value || '';
        this._dropdownButton.focus();
        emit(this, 'dropdown-update', { detail: { element: this, value: this.value } });
    }
    hoverItem() {
        const currentFocusedItem = this._dropdownItemsBox.querySelector('li.focused');
        if (currentFocusedItem) {
            currentFocusedItem.classList.remove('focused');
        }
    }
    dropdownKeyHandler(event) {
        const { key } = event;
        const upArrowPressed = key === 'ArrowUp';
        const downArrowPressed = key === 'ArrowDown';
        const excapePressed = key === 'Escape';
        const homePressed = key === 'Home';
        const endPressed = key === 'End';
        const enterPressed = key === 'Enter';
        if (!upArrowPressed && !downArrowPressed && !excapePressed && !homePressed && !endPressed && !enterPressed)
            return;
        event.preventDefault();
        if (excapePressed) {
            this.hideDropdown();
            this._dropdownButton.focus();
            return;
        }
        const optionsElement = this._dropdownItemsBox.querySelectorAll('[role=option]');
        const activeItem = this._dropdownItemsBox.querySelector('li.selected');
        const firstOptionElement = optionsElement[0];
        const lastOptionElement = optionsElement[optionsElement.length - 1];
        const nextActiveItem = (activeItem ? activeItem.nextElementSibling : firstOptionElement);
        const prevActiveItem = (activeItem ? activeItem.previousElementSibling : '');
        if (enterPressed) {
            this.focusItem(activeItem, 'enter');
        }
        if (homePressed) {
            this.focusItem(firstOptionElement, 'home');
        }
        if (endPressed) {
            this.focusItem(lastOptionElement, 'end');
        }
        if (downArrowPressed) {
            this.focusItem(nextActiveItem, 'next');
        }
        if (upArrowPressed) {
            this.focusItem(prevActiveItem, 'prev');
        }
    }
    selectDropdownItem(event) {
        const { target } = event;
        if (target.getAttribute('role') === 'option') {
            this.selectItem(target);
            this.hideDropdown();
        }
    }
    renderItems(idName) {
        return html `${this.initialChildren.map((item, index) => {
            const selected = item.hasAttribute('selected');
            const value = item.getAttribute('value');
            const { textContent } = item;
            if (item.matches('cipublic-dropdown-item')) {
                if (selected) {
                    return html `<li id="${idName}-option-${index}" role="option" aria-selected="true" class="selected" data-value="${ifDefined(value)}">${textContent}</li>`;
                }
                else {
                    return html `<li id="${idName}-option-${index}" role="option" data-value="${ifDefined(value)}">${textContent}</li>`;
                }
            }
            return;
        })}`;
    }
    renderDropdownItems(labelId) {
        if (this.initialChildren.length > 0) {
            const name = 'dropdown';
            const idName = `${name}-${counter++}`;
            return html `<button type="button" class="dropdown-trigger" aria-haspopup="listbox" aria-labelledby="${name}-button-${counter} ${labelId}" @click="${this.toggleDropdown}" id="${name}-button-${counter}">${unsafeHTML(this.buttonText)}</button><ul role="listbox" tabindex="-1" class="hidden dropdownItems" aria-labelledby="${labelId}" @click="${this.selectDropdownItem}" @blur="${this.hideDropdown}" @keydown="${this.dropdownKeyHandler}">${this.renderItems(idName)}</ul>`;
        }
        return html ``;
    }
    render() {
        const { label } = this;
        const idName = `${this._name}-${this._id}`;
        return html `<div class="element-wrapper"><label id="${idName}">${label}</label><div class="dropdown-wrapper">${this.renderDropdownItems(idName)}</div></div>`;
    }
}
Dropdown.styles = styles;
__decorate([
    property({ reflect: true })
], Dropdown.prototype, "value", void 0);
__decorate([
    property({ reflect: true })
], Dropdown.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], Dropdown.prototype, "label", void 0);
__decorate([
    state()
], Dropdown.prototype, "buttonText", void 0);
__decorate([
    query('button.dropdown-trigger')
], Dropdown.prototype, "_dropdownButton", void 0);
__decorate([
    query('ul.dropdownItems')
], Dropdown.prototype, "_dropdownItemsBox", void 0);
//# sourceMappingURL=dropdown.js.map